import React, { forwardRef, useId } from 'react';
import { theme } from '../../theme';

import { tokenVars } from '../../theme/tokens';
import { withAlpha } from '../../theme/colorUtils';
export interface FormInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string;
  error?: string;
  hint?: string;
  size?: 'small' | 'medium' | 'large';
  variant?: 'default' | 'search' | 'filter';
  required?: boolean;
  fullWidth?: boolean;
}

export const FormInput = forwardRef<HTMLInputElement, FormInputProps>(
  (
    // onFocus/onBlur are taken out of props and called from the input's own
    // handlers: spread after them, a caller's handler replaced the focus-style
    // cleanup and left the field looking focused.
    {
      label,
      error,
      hint,
      size = 'medium',
      variant = 'default',
      required = false,
      fullWidth = true,
      className = '',
      style = {},
      onFocus,
      onBlur,
      ...props
    },
    ref
  ) => {
    const sizeConfig = theme.sizes.getSizeConfig(size);
    // The label names the input (screen readers, getByLabel): tie them with an
    // id, the caller's when it passes one.
    const generatedId = useId();
    const inputId = props.id ?? generatedId;

    const variants = {
      default: {
        backgroundColor: tokenVars.bgSubtle,
        border: `1px solid ${tokenVars.border}`,
      },
      search: {
        backgroundColor: tokenVars.bgSubtle,
        border: `1px solid ${tokenVars.border}`,
      },
      filter: {
        backgroundColor: tokenVars.surface,
        border: `1px solid ${tokenVars.border}`,
      },
    };

    const inputStyles = {
      width: fullWidth ? '100%' : 'auto',
      backgroundColor: variants[variant].backgroundColor,
      border: variants[variant].border,
      borderRadius: theme.borders.input.radius,
      color: tokenVars.text.primary,
      outline: 'none',
      transition: `border-color ${theme.animations.duration.fast}ms ${theme.animations.easing.easeOut}`,
      fontFamily: theme.typography.fontFamily.primary,
      fontSize: sizeConfig.fontSize,
      lineHeight: theme.typography.lineHeight.normal,
      padding: sizeConfig.padding,
      minHeight: sizeConfig.minHeight,
      ...style,
    };

    const focusStyles = {
      borderColor: tokenVars.focus, // Blue focus (Style Guide)
      boxShadow: `0 0 0 2px ${withAlpha(tokenVars.focus, 0.25)}`,
    };

    const errorStyles = error
      ? {
          borderColor: tokenVars.danger,
          boxShadow: `0 0 0 2px ${withAlpha(tokenVars.danger, 0.25)}`,
        }
      : {};

    const disabledStyles = props.disabled
      ? {
          backgroundColor: tokenVars.surface,
          color: tokenVars.text.secondary,
          cursor: 'not-allowed',
          opacity: 0.8,
          border: `1px solid ${tokenVars.border}`,
        }
      : {};

    const labelStyles = {
      display: 'block',
      marginBottom: theme.spacing[1],
      color: tokenVars.text.secondary,
      fontSize: theme.typography.fontSize.sm,
      fontWeight: theme.typography.fontWeight.medium,
      fontFamily: theme.typography.fontFamily.primary,
      textAlign: 'left' as const,
    };

    const hintStyles = {
      marginTop: theme.spacing[1],
      color: tokenVars.text.secondary,
      fontSize: theme.typography.fontSize.xs,
      fontFamily: theme.typography.fontFamily.primary,
    };

    const errorTextStyles = {
      marginTop: theme.spacing[1],
      color: tokenVars.dangerText,
      fontSize: theme.typography.fontSize.xs,
      fontFamily: theme.typography.fontFamily.primary,
    };

    return (
      <div style={{ width: fullWidth ? '100%' : 'auto' }}>
        {label && (
          <label htmlFor={inputId} style={labelStyles}>
            {label}
            {/* Shown, not read: the field's name is the label alone. */}
            {required && (
              <span aria-hidden="true" style={{ color: tokenVars.dangerText }}>
                {' '}
                *
              </span>
            )}
          </label>
        )}
        <input
          ref={ref}
          style={{
            ...inputStyles,
            ...errorStyles,
            ...disabledStyles,
          }}
          className={className}
          onFocus={(e) => {
            if (!props.disabled) {
              Object.assign(e.target.style, focusStyles);
              onFocus?.(e);
            }
          }}
          onBlur={(e) => {
            if (!props.disabled) {
              Object.assign(e.target.style, {
                borderColor: error ? tokenVars.danger : tokenVars.border,
                boxShadow: error ? `0 0 0 2px ${withAlpha(tokenVars.danger, 0.25)}` : 'none',
              });
              onBlur?.(e);
            }
          }}
          {...props}
          // After the spread: an explicit id={undefined} must not untie the label.
          id={inputId}
          // The asterisk is not read out; say "required" here instead.
          aria-required={required || undefined}
        />
        {hint && !error && <div style={hintStyles}>{hint}</div>}
        {error && <div style={errorTextStyles}>{error}</div>}
      </div>
    );
  }
);
